import { buildAppScheme } from '@/shared/lib/getAppScheme'
import { ROUTES } from '@/shared/lib/routes'

export const APP_SCHEME = {
  // 앱 기록 데이터 동기화
  get FETCH_DIARY() {
    return buildAppScheme('/fetchData?dataType=diary')
  },

  // 주유 리포트 랜딩
  REFUEL_REPORT: (macarId: number, diaryId: number) =>
    buildAppScheme(
      `/report/refuelReportView?macarId=${macarId}&diaryId=${diaryId}`
    ),

  OPEN_SHARE_MORE: (shareUrl: string) =>
    buildAppScheme(`/open/share?url=${encodeURIComponent(shareUrl)}`),

  // 세차 예약
  get RESERVE_DETAILING() {
    return buildAppScheme(
      '/targetproduct/groups?groupTypes=디테일링&exposureArea=web'
    )
  },

  // 타이어 교체 예약
  get RESERVE_TIRE() {
    return buildAppScheme('/targetproduct/groups?groupTypes=타이어_마이클_배송')
  },

  // 엔진오일 예약 — 사용자 차의 oil_set 그룹 중 첫 번째로 진입 (groups 방식은 정확히 1개 일치일 때만 진입)
  get RESERVE_ENGINE_OIL() {
    return buildAppScheme('/targetproduct/section?section=oil_set')
  },

  // 정비 기록 목록 (전체)
  get DIARY_INPUT() {
    return buildAppScheme('/diaryinput/1')
  },

  // 주유 기록
  get REFUEL_INPUT() {
    return buildAppScheme('/diaryinput/0')
  },

  // 정비 예약 탭
  get SERVICE_TAB() {
    return buildAppScheme('/selecttab/service')
  },

  // 홈 탭
  get MAIN_TAB() {
    return buildAppScheme('/selecttab/main')
  },

  /**
   * 정비소 상세 (앱 내 웹뷰)
   *
   * `tireWebReservation` 은 타이어 웹예약에서 들어왔다는 표시다 — 앱이 그걸 보고
   * 돌아올 길을 잡는다. 그 밖에서는 붙이지 않는다.
   * `latitude`·`longitude` 는 목록이 거리를 잰 기준 좌표다. 앱이 이 좌표로 상세 거리를 다시 잰다
   * (고른 지역 기준이면 목록과 상세 거리가 같아야 한다). 받지 못하는 앱은 무시한다
   */
  SERVICE_DETAIL: (
    placeId: number,
    options?: {
      fromTireWebReservation?: boolean
      latitude?: number
      longitude?: number
    }
  ) => {
    const query = new URLSearchParams()
    if (options?.fromTireWebReservation) query.set('tireWebReservation', 'true')
    if (options?.latitude != null && options?.longitude != null) {
      query.set('latitude', String(options.latitude))
      query.set('longitude', String(options.longitude))
    }
    const search = query.toString()

    return buildAppScheme(
      `/service/detail/${placeId}${search ? `?${search}` : ''}`
    )
  },

  /**
   * 예약 완료 화면 (앱 화면)
   *
   * 결제가 끝나면 웹이 아니라 앱 화면으로 넘긴다 — 완료 뒤의 동선은 앱이 들고 있다.
   * `usedOnsiteCoupon` 은 현장결제 쿠폰(`DiscountCodeZero100`)을 썼는지로, 앱이 그에 맞는
   * 안내를 띄운다.
   */
  RESERVATION_COMPLETE: ({
    reservationId,
    usedOnsiteCoupon,
  }: {
    reservationId: number
    usedOnsiteCoupon: boolean
  }) =>
    buildAppScheme(
      `/reservation/complete?reservationId=${reservationId}&usedOnsiteCoupon=${usedOnsiteCoupon}`
    ),

  // 쿠폰함 랜딩
  get COUPON() {
    return buildAppScheme('/coupon')
  },

  // 주행 기록 랜딩
  get DIARY_DISTANCE() {
    return buildAppScheme('/diaryinput/3')
  },

  // 주행 기록 작성
  get DIARY_DISTANCE_INPUT() {
    return buildAppScheme('/distance')
  },

  // 주유 기록 랜딩
  get DIARY_REFUEL() {
    return buildAppScheme('/diaryinput/1')
  },

  // 정비 리스트 랜딩
  get MAINTENANCE_LIST() {
    return buildAppScheme('/maintenance/list')
  },

  // 정비 리스트 랜딩 (교체 주기 변경 후 진입시)
  get MAINTENANCE_LIST_WITH_REFRESH() {
    return buildAppScheme('/maintenance/list?refresh=true')
  },

  // 내 차 정보 화면 랜딩
  MY_CAR_INFO: ({ macarId, from }: { macarId: string; from?: string }) =>
    buildAppScheme(
      `/launch/MycleCarInfo?macarId=${macarId}${from ? `&from=${from}` : ''}`
    ),

  // 내 차 정보 > 리콜 랜딩
  MY_CAR_RECALL: ({ macarId, from }: { macarId: string; from?: string }) =>
    buildAppScheme(
      `/launch/MycleCarInfo/recall?macarId=${macarId}${from ? `&from=${from}` : ''}`
    ),

  // 자동차 보험 화면
  INSURANCE: (from?: string) =>
    buildAppScheme(`/insurance${from ? `?from=${from}` : ''}`),

  /**
   * 정비소 리뷰 목록 (앱 화면)
   *
   * `tireWebReservation` 은 타이어 웹예약에서 들어왔다는 표시다.
   */
  SERVICE_REVIEWS: (
    placeId: number,
    options?: { fromTireWebReservation?: boolean }
  ) =>
    buildAppScheme(
      `/service/reviews/${placeId}${
        options?.fromTireWebReservation ? '?tireWebReservation=true' : ''
      }`
    ),

  // 주유권 리스트 랜딩
  get GAS_VOUCHERS() {
    return buildAppScheme(
      `/open/gaspromotion?link=${process.env.NEXT_PUBLIC_WEB_SERVICE_URL}${process.env.NEXT_PUBLIC_BASE_PATH}${ROUTES.GAS_VOUCHERS}`
    )
  },
}
